<template>
  <div>
    <b-form-group
      v-if="field"
      :label="field.label + (field.required ? ' *' : '')"
      label-class="pt-0"
      label-cols-sm="4"
      label-cols-lg="3"
      :description="field.help_text"
      :invalid-feedback="String(error)"
      :state="error ? false : undefined"
    >
      <b-form-radio-group
        id="select-mode"
        :required="field.required"
        :disabled="field.read_only"
        v-model="accountData.profileType"
        @input="onChangeType"
        :options="field.choices"
        text-field="display_name"
        :state="error ? false : undefined"
      />
    </b-form-group>
    <OptionsFormApi
      ref="form"
      :api="api"
      :options-api="optionsApi"
      id-key="token"
      v-model="accountData"
      :omitFields="omitFields"
      :modifyFields="modifyFields"
      :modifyErrors="modifyErrors"
      @success="$emit('success', $event)"
    >
      <!-- pass all slots from parent component -->
      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope"/>
      </template>
    </OptionsFormApi>
  </div>
</template>

<script>
import { pick } from 'lodash'

import OptionsFormApi from '@/components/OptionsFormApi.vue'
import { formatErrors, sensitiveFields } from './HWShared.js'
import { http } from '@/services/http.js'
import { omit } from '@/utils/omit.js'

const fieldSets = {
  individual: [
    'firstName',
    'lastName',
    'dateOfBirth',
  ],
  business: [
    'businessName',
  ],
}
const dynamicFields = [
  ...fieldSets.individual,
  ...fieldSets.business,
]

export default {
  name: 'HWAccount',
  components: {
    OptionsFormApi,
  },
  props: {
    value: Object,
    omitFields: Function,
    beforeModifyFields: Function,
  },
  data() {
    return {
      accountData: {
        profileType: 'INDIVIDUAL',
      },
      fields: {},
      errors: {},
    }
  },
  computed: {
    api() {
      const token = this.accountData.token
      if (token) { return 'artist-identity' }
      return this.optionsApi
    },
    optionsApi() {
      const type = this.accountData.profileType;
      if (!type) { return 'artist-identity' }
      const country = {
        'US': 'US',
      }[this.accountData.country]
      const name = [country, type].filter(i => i).join('_').toLowerCase()
      return `artist-identity/${name}`
    },
    field() {
      const field = this.fields.profileType
      if (!field) { return }
      return {
        ...field,
        read_only: Boolean(this.accountData.token),
      }
    },
    error() {
      return this.errors.profileType
    },
  },
  watch: {
    value: {
      handler(val) {
        if (!val) { return }
        this.accountData = val
      },
      immediate: true,
    },
  },
  async created() {
    this.fetchFields()
  },
  methods: {
    async onSubmit() {
      this.errors = {}
      this.accountData = omitHidden(this.accountData)
      await this.$nextTick() // allow accountData to propagate to form
      return this.$refs.form.onSubmit()
    },
    onChangeType() {
      this.errors.profileType = undefined
    },
    async fetchFields() {
      this.fields = await http.options('/api/artist-identity/')
        .then(response => response.data.actions.POST)
    },
    modifyErrors(data) {
      let errors = formatErrors(data)
      this.errors = errors
      return errors
    },
    modifyFields(fields) {
      if (this.beforeModifyFields) {
        fields = this.beforeModifyFields(fields)
      }
      // visually mask sensitive fields
      sensitiveFields.user.forEach(i => {
        if (fields[i]) {
          fields[i] = {
            ...fields[i],
            type: 'masked',
            props: {maskOptions: {showLast: 2}},
          }
        }
      })
      //reorder fields
      const first = pick(fields, dynamicFields)
      return {...first, ...fields}
    },
  },
}

function omitHidden(accountData) {
  const toOmit = accountData.profileType === 'INDIVIDUAL' ? 'business' : 'individual'
  return omit(accountData, fieldSets[toOmit])
}
</script>
